import { FC } from "react";
import { useTranslation } from "react-i18next";

import {
  useBookingsByIdsNavigation,
  useRidesByIdsNavigation,
} from "@ryde/redux";
import {
  BulkProduceTypeEnum,
  onActivateKeyDown,
  useDrawers,
} from "@ryde/shared";
import { greenMui, rydeColors, tealMui } from "@ryde/theme";

import { PreviewDrawerLineStyled } from "../../misc";
import { ProductionHubStatisticsLines } from "./production-hub-statistics-lines";
import { useProductionPreviewStatistics } from "./production-preview-statistics.hook";

interface IProductionPreviewStatisticsProps {
  action: BulkProduceTypeEnum;
}

export const ProductionPreviewStatistics: FC<
  IProductionPreviewStatisticsProps
> = ({ action }) => {
  const { t } = useTranslation();
  const { navigateToRidesByIds } = useRidesByIdsNavigation();
  const { navigateToBookingsByIds } = useBookingsByIdsNavigation();
  const { openCourseSchedulesPreviewDrawer } = useDrawers();

  const {
    totalBookingCount,
    allInputBookingIds,
    bookingsAlreadyInProduceCount,
    bookingsAlreadyInProduceIds,
    joinedBookingCount,
    joinedRideCount,
    producedRideCount,
    producedRideIds,
    failedToSaveJoinedBookingCount,
    failedToSaveJoinedBookingIds,
    failedToSaveJoinedRideCount,
    failedToSaveJoinedRideIds,
    producedBookingCount,
    producedBookingIds,
    rideTooShortBookingCount,
    rideTooShortBookingIds,
    joinedBookingIds,
    joinedRideIds,
    producedAndJoinedRideIds,
    joinedSentRideSupplierCanceledIds,
    joinedSentRideSupplierCanceledCount,
    joinedSentRideSupplierUpdatedIds,
    joinedSentRideSupplierUpdatedCount,
    courseJoinedSentRideSupplierCanceledIds,
    courseJoinedSentRideSupplierCanceledCount,
    courseJoinedSentRideSupplierUpdatedIds,
    courseJoinedSentRideSupplierUpdatedCount,
    coursesSkippedOnNoPassengersCount,
    coursesSkippedOnNoPassengers,
    coursesAlreadyExistCount,
    coursesAlreadyExist,
    producedCoursesCount,
    producedCourses,
    notSavedCoursesCount,
    notSavedCourses,
    unmatchedBookingsDisplayCount,
    unmatchedBookingsDisplayIds,
    unmatchedBookingsLabelKey,
    savedRidesCount,
    producedButNotSavedRidesCount,
    hubBookingIds,
    hubBookingCount,
    nonHubBookingCount,
    hubMainRideIds,
    hubMainRideCount,
    hubPersonalRideIds,
    hubPersonalRideCount,
    hubExpectedMainRideCount,
    hubExpectedPersonalRideCount,
    hubBookingsSkippedInSplitIds,
    hubBookingsSkippedInSplitCount,
    hubPersonalGeoFailedBookingIds,
    hubPersonalGeoFailedCount,
    hubBookingsWithMainSegmentIds,
    hubBookingsWithMainSegmentCount,
    hubIncompletePairingBookingIds,
    hubIncompletePairingCount,
    hubBookingsProducedIds,
    hubBookingsProducedCount,
    hubCompanionBookingIds,
    hubCompanionBookingCount,
    hubBookingsExcludedFromJoinIds,
    hubExplicitSkipBookingIds,
    hubExplicitSkipBookingCount,
  } = useProductionPreviewStatistics();

  const shouldShowJoinSupplierSyncStats =
    action === BulkProduceTypeEnum.Join || action === BulkProduceTypeEnum.Both;

  const shouldShowCourseSupplierSyncStats =
    action === BulkProduceTypeEnum.Produce ||
    action === BulkProduceTypeEnum.Both;

  return (
    <>
      <PreviewDrawerLineStyled>
        <span>{t("rideProduction.bookingProcessed")}</span>
        {allInputBookingIds?.length ? (
          <span
            className="preview-drawer-line__value--clickable"
            role="button"
            tabIndex={0}
            onClick={() => navigateToBookingsByIds(allInputBookingIds)}
            onKeyDown={onActivateKeyDown(() =>
              navigateToBookingsByIds(allInputBookingIds),
            )}
          >
            {totalBookingCount}
          </span>
        ) : (
          <span>{totalBookingCount}</span>
        )}
      </PreviewDrawerLineStyled>
      {action !== BulkProduceTypeEnum.Produce && (
        <>
          <PreviewDrawerLineStyled $numberColor={tealMui._600}>
            <span>{t("rideProduction.joinedBookings")}</span>
            {joinedBookingIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() => navigateToBookingsByIds(joinedBookingIds)}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(joinedBookingIds),
                )}
              >
                {joinedBookingCount}
              </span>
            ) : (
              <span>{joinedBookingCount}</span>
            )}
          </PreviewDrawerLineStyled>
          <PreviewDrawerLineStyled $numberColor={tealMui._600}>
            <span>{t("rideProduction.joinedRides")}</span>
            {joinedRideIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() => navigateToRidesByIds(joinedRideIds)}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(joinedRideIds),
                )}
              >
                {joinedRideCount}
              </span>
            ) : (
              <span>{joinedRideCount}</span>
            )}
          </PreviewDrawerLineStyled>
        </>
      )}
      {action !== BulkProduceTypeEnum.Join && (
        <>
          <PreviewDrawerLineStyled $numberColor={rydeColors.green}>
            <span>{t("rideProduction.producedBookings")}</span>
            {producedBookingIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() => navigateToBookingsByIds(producedBookingIds)}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToBookingsByIds(producedBookingIds),
                )}
              >
                {producedBookingCount}
              </span>
            ) : (
              <span>{producedBookingCount}</span>
            )}
          </PreviewDrawerLineStyled>
          <PreviewDrawerLineStyled $numberColor={rydeColors.green}>
            <span>{t("rideProduction.producedRides")}</span>
            {producedRideIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() => navigateToRidesByIds(producedRideIds)}
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(producedRideIds),
                )}
              >
                {producedRideCount}
              </span>
            ) : (
              <span>{producedRideCount}</span>
            )}
          </PreviewDrawerLineStyled>
          <ProductionHubStatisticsLines
            hubBookingIds={hubBookingIds}
            hubBookingCount={hubBookingCount}
            nonHubBookingCount={nonHubBookingCount}
            hubMainRideIds={hubMainRideIds}
            hubMainRideCount={hubMainRideCount}
            hubPersonalRideIds={hubPersonalRideIds}
            hubPersonalRideCount={hubPersonalRideCount}
            hubExpectedMainRideCount={hubExpectedMainRideCount}
            hubExpectedPersonalRideCount={hubExpectedPersonalRideCount}
            hubBookingsSkippedInSplitIds={hubBookingsSkippedInSplitIds}
            hubBookingsSkippedInSplitCount={hubBookingsSkippedInSplitCount}
            hubPersonalGeoFailedBookingIds={hubPersonalGeoFailedBookingIds}
            hubPersonalGeoFailedCount={hubPersonalGeoFailedCount}
            hubBookingsWithMainSegmentIds={hubBookingsWithMainSegmentIds}
            hubBookingsWithMainSegmentCount={hubBookingsWithMainSegmentCount}
            hubIncompletePairingBookingIds={hubIncompletePairingBookingIds}
            hubIncompletePairingCount={hubIncompletePairingCount}
            hubBookingsProducedIds={hubBookingsProducedIds}
            hubBookingsProducedCount={hubBookingsProducedCount}
            hubCompanionBookingIds={hubCompanionBookingIds}
            hubCompanionBookingCount={hubCompanionBookingCount}
            hubBookingsExcludedFromJoinIds={hubBookingsExcludedFromJoinIds}
            hubExplicitSkipBookingIds={hubExplicitSkipBookingIds}
            hubExplicitSkipBookingCount={hubExplicitSkipBookingCount}
          />
        </>
      )}
      {Boolean(savedRidesCount) && (
        <PreviewDrawerLineStyled $numberColor={greenMui._800}>
          <span className="saved-rides__label">
            {t("rideProduction.savedRides")}
          </span>
          {producedAndJoinedRideIds?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                navigateToRidesByIds(producedAndJoinedRideIds || [])
              }
              onKeyDown={onActivateKeyDown(() =>
                navigateToRidesByIds(producedAndJoinedRideIds || []),
              )}
            >
              {savedRidesCount}
            </span>
          ) : (
            <span>{savedRidesCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {Boolean(producedButNotSavedRidesCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.red}>
          <span className="not-saved-rides__label">
            {t("rideProduction.notSavedRides")}
          </span>
          <span>{producedButNotSavedRidesCount}</span>
        </PreviewDrawerLineStyled>
      )}
      {Boolean(rideTooShortBookingCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.skippedBookingsRideTooShort")}</span>
          {rideTooShortBookingIds?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() => navigateToBookingsByIds(rideTooShortBookingIds)}
              onKeyDown={onActivateKeyDown(() =>
                navigateToBookingsByIds(rideTooShortBookingIds),
              )}
            >
              {rideTooShortBookingCount}
            </span>
          ) : (
            <span>{rideTooShortBookingCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {unmatchedBookingsDisplayCount > 0 && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t(unmatchedBookingsLabelKey)}</span>
          {unmatchedBookingsDisplayIds?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                navigateToBookingsByIds(unmatchedBookingsDisplayIds)
              }
              onKeyDown={onActivateKeyDown(() =>
                navigateToBookingsByIds(unmatchedBookingsDisplayIds),
              )}
            >
              {unmatchedBookingsDisplayCount}
            </span>
          ) : (
            <span>{unmatchedBookingsDisplayCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {Boolean(coursesSkippedOnNoPassengersCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.coursesSkippedOnNoPassengers")}</span>
          {coursesSkippedOnNoPassengers?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: coursesSkippedOnNoPassengers,
                  title: t("rideProduction.coursesSkippedOnNoPassengers"),
                })
              }
              onKeyDown={onActivateKeyDown(() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: coursesSkippedOnNoPassengers,
                  title: t("rideProduction.coursesSkippedOnNoPassengers"),
                }),
              )}
            >
              {coursesSkippedOnNoPassengersCount}
            </span>
          ) : (
            <span>{coursesSkippedOnNoPassengersCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {Boolean(coursesAlreadyExistCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.coursesAlreadyExist")}</span>
          {coursesAlreadyExist?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: coursesAlreadyExist,
                  title: t("rideProduction.coursesAlreadyExist"),
                })
              }
              onKeyDown={onActivateKeyDown(() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: coursesAlreadyExist,
                  title: t("rideProduction.coursesAlreadyExist"),
                }),
              )}
            >
              {coursesAlreadyExistCount}
            </span>
          ) : (
            <span>{coursesAlreadyExistCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {Boolean(producedCoursesCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.green}>
          <span>{t("rideProduction.producedCourses")}</span>
          {producedCourses?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: producedCourses,
                  title: t("rideProduction.producedCourses"),
                })
              }
              onKeyDown={onActivateKeyDown(() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: producedCourses,
                  title: t("rideProduction.producedCourses"),
                }),
              )}
            >
              {producedCoursesCount}
            </span>
          ) : (
            <span>{producedCoursesCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {Boolean(notSavedCoursesCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.red}>
          <span>{t("rideProduction.notSavedCourses")}</span>
          {notSavedCourses?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: notSavedCourses,
                  title: t("rideProduction.notSavedCourses"),
                })
              }
              onKeyDown={onActivateKeyDown(() =>
                openCourseSchedulesPreviewDrawer({
                  schedules: notSavedCourses,
                  title: t("rideProduction.notSavedCourses"),
                }),
              )}
            >
              {notSavedCoursesCount}
            </span>
          ) : (
            <span>{notSavedCoursesCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {Boolean(bookingsAlreadyInProduceCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
          <span>{t("rideProduction.bookingsAlreadyInProduce")}</span>
          {bookingsAlreadyInProduceIds?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                navigateToBookingsByIds(bookingsAlreadyInProduceIds)
              }
              onKeyDown={onActivateKeyDown(() =>
                navigateToBookingsByIds(bookingsAlreadyInProduceIds),
              )}
            >
              {bookingsAlreadyInProduceCount}
            </span>
          ) : (
            <span>{bookingsAlreadyInProduceCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {Boolean(failedToSaveJoinedBookingCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.red}>
          <span>{t("rideProduction.failedToSaveJoinedBookings")}</span>
          {failedToSaveJoinedBookingIds?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() =>
                navigateToBookingsByIds(failedToSaveJoinedBookingIds)
              }
              onKeyDown={onActivateKeyDown(() =>
                navigateToBookingsByIds(failedToSaveJoinedBookingIds),
              )}
            >
              {failedToSaveJoinedBookingCount}
            </span>
          ) : (
            <span>{failedToSaveJoinedBookingCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
      {shouldShowJoinSupplierSyncStats &&
        Boolean(joinedSentRideSupplierCanceledCount) && (
          <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
            <span>{t("rideProduction.sentRideSupplierCanceledAtJoin")}</span>
            {joinedSentRideSupplierCanceledIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToRidesByIds(joinedSentRideSupplierCanceledIds)
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(joinedSentRideSupplierCanceledIds),
                )}
              >
                {joinedSentRideSupplierCanceledCount}
              </span>
            ) : (
              <span>{joinedSentRideSupplierCanceledCount}</span>
            )}
          </PreviewDrawerLineStyled>
        )}
      {shouldShowJoinSupplierSyncStats &&
        Boolean(joinedSentRideSupplierUpdatedCount) && (
          <PreviewDrawerLineStyled $numberColor={tealMui._600}>
            <span>{t("rideProduction.sentRideSupplierUpdatedAtJoin")}</span>
            {joinedSentRideSupplierUpdatedIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToRidesByIds(joinedSentRideSupplierUpdatedIds)
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(joinedSentRideSupplierUpdatedIds),
                )}
              >
                {joinedSentRideSupplierUpdatedCount}
              </span>
            ) : (
              <span>{joinedSentRideSupplierUpdatedCount}</span>
            )}
          </PreviewDrawerLineStyled>
        )}
      {shouldShowCourseSupplierSyncStats &&
        Boolean(courseJoinedSentRideSupplierCanceledCount) && (
          <PreviewDrawerLineStyled $numberColor={rydeColors.orange}>
            <span>
              {t("rideProduction.courseSentRideSupplierCanceledAtProduce")}
            </span>
            {courseJoinedSentRideSupplierCanceledIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToRidesByIds(courseJoinedSentRideSupplierCanceledIds)
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(courseJoinedSentRideSupplierCanceledIds),
                )}
              >
                {courseJoinedSentRideSupplierCanceledCount}
              </span>
            ) : (
              <span>{courseJoinedSentRideSupplierCanceledCount}</span>
            )}
          </PreviewDrawerLineStyled>
        )}
      {shouldShowCourseSupplierSyncStats &&
        Boolean(courseJoinedSentRideSupplierUpdatedCount) && (
          <PreviewDrawerLineStyled $numberColor={tealMui._600}>
            <span>
              {t("rideProduction.courseSentRideSupplierUpdatedAtProduce")}
            </span>
            {courseJoinedSentRideSupplierUpdatedIds?.length ? (
              <span
                className="preview-drawer-line__value--clickable"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigateToRidesByIds(courseJoinedSentRideSupplierUpdatedIds)
                }
                onKeyDown={onActivateKeyDown(() =>
                  navigateToRidesByIds(courseJoinedSentRideSupplierUpdatedIds),
                )}
              >
                {courseJoinedSentRideSupplierUpdatedCount}
              </span>
            ) : (
              <span>{courseJoinedSentRideSupplierUpdatedCount}</span>
            )}
          </PreviewDrawerLineStyled>
        )}
      {Boolean(failedToSaveJoinedRideCount) && (
        <PreviewDrawerLineStyled $numberColor={rydeColors.red}>
          <span>{t("rideProduction.failedToSaveJoinedRides")}</span>
          {failedToSaveJoinedRideIds?.length ? (
            <span
              className="preview-drawer-line__value--clickable"
              role="button"
              tabIndex={0}
              onClick={() => navigateToRidesByIds(failedToSaveJoinedRideIds)}
              onKeyDown={onActivateKeyDown(() =>
                navigateToRidesByIds(failedToSaveJoinedRideIds),
              )}
            >
              {failedToSaveJoinedRideCount}
            </span>
          ) : (
            <span>{failedToSaveJoinedRideCount}</span>
          )}
        </PreviewDrawerLineStyled>
      )}
    </>
  );
};
